import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import {
  isSettingsNavPathV3,
  settingsNavPathV3,
} from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { Link, useLocation, useMatchRoute } from "@tanstack/react-router";
import type { RegisteredRouter } from "@tanstack/react-router";
import type { RouteToPath } from "@tanstack/router-core";
import { values } from "lodash";
import { type ReactNode, useCallback } from "react";

import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";

import type { SidebarLinkPathKey } from "./types";
import { BACK_TO_COMMUNITY_PATH_KEY } from "./useCollapsedSidebarLinksV3";

// Re-export under the local alias the rest of the sidebar already imports.
// `SidebarLinkPathKey` is the source of truth (decoupled from
// `settingsNavPathV3` so per-key migrations don't shrink the union).
export type PathKey = SidebarLinkPathKey;

export interface NavLinkWrapperProps {
  children: ReactNode;
  path: string;
  pathKey?: PathKey;
}

export const NavLinkWrapper = ({
  children,
  path,
  pathKey,
}: NavLinkWrapperProps) => {
  const { currentCommunity } = usePunditUserContext();
  const { trackEvent } = useEventsTracker();
  const pathname = useLocation({ select: location => location.pathname });
  const matchRoute = useMatchRoute();

  const handleClick = useCallback(() => {
    if (pathKey === "admin_dashboard") {
      trackEvent("admin_dashboard.sidebar.clicked", {});
    }
  }, [pathKey, trackEvent]);

  const shouldRenderTraditionalAnchorTag =
    pathKey === BACK_TO_COMMUNITY_PATH_KEY &&
    !currentCommunity?.home_page_enabled;

  const links = settingsNavPathV3;

  if (!shouldRenderTraditionalAnchorTag) {
    const hasMatch = Boolean(
      matchRoute({
        to: path as RouteToPath<RegisteredRouter>,
        fuzzy: false,
      }),
    );
    const routes = isSettingsNavPathV3(pathKey) ? links[pathKey] : null;
    const isPathInLocation =
      routes &&
      values(routes).some(route => {
        const isCommunityInboxPage = pathname.startsWith(
          settingsNavPathV3.community_inbox.index,
        );
        if (isCommunityInboxPage) {
          return (route as string) === settingsNavPathV3.community_inbox.index;
        }
        return pathname === route;
      });
    const isActive = hasMatch || Boolean(isPathInLocation);

    return (
      <Link
        to={path}
        className={classNames(
          "hover:bg-secondary hover:border-dark flex items-center justify-center rounded-md border border-transparent p-2.5 transition-all duration-150 ease-linear",
          isActive && "bg-tertiary",
        )}
        onClick={handleClick}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      href={path}
      className="hover:bg-secondary hover:border-dark active:bg-tertiary flex items-center justify-center rounded-md border border-transparent p-2.5 transition-all duration-150 ease-linear"
      onClick={handleClick}
    >
      {children}
    </a>
  );
};
